import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import { cx } from '@/lib/cx'

interface Toast {
  id: number
  message: string
  tone: 'default' | 'danger'
}

interface ToastApi {
  show: (message: string) => void
  showError: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback((message: string, tone: Toast['tone']) => {
    const id = Date.now() + Math.random()
    setToasts((current) => [...current, { id, message, tone }])
    // Errors that lost data must never be announced only here — a toast that
    // fades is not a channel for anything the doctor has to act on.
    window.setTimeout(() => setToasts((c) => c.filter((t) => t.id !== id)), 5000)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      show: (message) => push(message, 'default'),
      showError: (message) => push(message, 'danger'),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Pinned to both edges rather than centred by a transform: `left: 50%`
          resolves against the containing block while a viewport width resolves
          against the viewport, and on a phone the two diverge the moment
          anything overflows — the region then holds the page wide and taps land
          beside their target.
          Announced politely, because "Visit saved" must reach a screen reader. */}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 px-4 pb-safe-6"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              'pointer-events-auto flex w-full max-w-sheet items-center gap-3 rounded-md py-3 pr-3 pl-4 text-sm leading-snug shadow-lg',
              toast.tone === 'danger' ? 'bg-danger text-on-danger' : 'bg-fg text-surface',
            )}
          >
            {toast.message}
            <button
              type="button"
              className="ml-auto grid min-h-touch min-w-touch flex-none place-items-center rounded-sm text-current opacity-75 hover:opacity-100"
              aria-label="Dismiss"
              onClick={() => setToasts((c) => c.filter((t) => t.id !== toast.id))}
            >
              <Icon name="close" size="1rem" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside ToastProvider')
  return api
}
